/**
 * The two brand themes. Obsidian is the standard mode; Canvas is the light
 * alternative — both ship from day one (issue #16, product sync 2026-07-27).
 *
 * This module is the single authority on what the current theme is: the
 * `data-theme` attribute on <html> drives every token in styles.css, and
 * localStorage makes the choice durable. Both apps' index.html run a tiny
 * inline copy of this logic before first paint (see THEME_STORAGE_KEY's doc)
 * so a Canvas user never sees an Obsidian flash — this module then adopts
 * whatever that script applied.
 */

import { THEMES, type Theme } from '@cusp/core';
import { readBrandCache, storedTenantId } from './brand.js';

/**
 * The list itself lives in `@cusp/core` — a school's brand carries a default
 * direction, so the value is on the wire and `@cusp/contracts` needs the enum,
 * and contracts may depend on core alone. Re-exported here so every existing
 * `import { THEMES } from '@cusp/theme'` keeps working.
 */
export { THEMES, type Theme };

export const THEME_LABELS: Record<Theme, string> = {
  obsidian: 'Dark',
  canvas: 'Light',
};

export const DEFAULT_THEME: Theme = 'obsidian';

/**
 * Also hardcoded in the no-flash inline script in each app's index.html —
 * keep the three in sync. Drift there is only cosmetic (a wrong first paint):
 * main.tsx calls initTheme(), which re-applies the stored preference.
 */
export const THEME_STORAGE_KEY = 'cusp.theme';

function isTheme(value: unknown): value is Theme {
  return (THEMES as readonly unknown[]).includes(value);
}

/**
 * THE SCHOOL'S DIRECTION, AND WHOSE IT IS.
 *
 * THE DIRECTION IS A DEFAULT AND NOT A PREFERENCE. A branded institution may
 * say "our people start in Canvas". That has to lose to anything the person in
 * front of the screen has actually chosen — a default that overrode a toggle
 * would be an admin reaching through the network to undo a reader's decision
 * every time they reload. Hence the precedence in `storedTheme()`:
 * stored ?? session default ?? Obsidian.
 *
 * IT CARRIES ITS OWNER FOR THE SAME REASON THE PAINT DOES (`AppliedBrand` in
 * `brand.ts` argues the point at length). A direction is derived from the
 * acting tenant, and three parties set it without meeting — `initTheme()` from
 * the first-paint cache before React exists, `useBrandSync` when the read
 * lands, and the handover a departing syncer leaves behind. Without an owner
 * "is this direction still the right one?" is unanswerable, and the answer that
 * gets assumed is "yes": a reader switches from a Canvas school to an Obsidian
 * one, the new school's read is still pending (or errors), and the old school's
 * Canvas frames the new school's data for as long as that takes. With the owner
 * recorded it is a comparison, and `useBrandSync` makes it on every run.
 *
 * It is per-session module state rather than storage on purpose. It is derived
 * from the acting tenant, so it must vanish when the tab does and must not
 * survive a workspace switch — persisting it would make it indistinguishable
 * from the user's own choice, which is the one thing it must never become.
 */
export interface SessionDefault {
  /** The direction the school chose. */
  theme: Theme;
  /**
   * The institution it was derived from, or null when the caller named none.
   *
   * An unowned direction is not a trusted one: `useBrandSync` clears any record
   * whose owner is not the tenant it is acting in, and null is never a tenant
   * id, so a default set without an owner survives only until the next syncer
   * run. That is deliberate — failing towards Cusp's own direction is the safe
   * side of a question nobody can answer.
   */
  tenantId: string | null;
}

let sessionDefaultRecord: SessionDefault | null = null;

/**
 * Set (or clear) the acting school's default direction, and record which
 * institution it belongs to. Applies immediately when — and only when — the
 * reader has no stored preference of their own.
 *
 * Correcting only the OWNER is deliberately silent: the document already
 * renders the right direction, so re-applying it and waking every subscriber
 * would be churn nobody can see.
 */
export function setSessionDefaultTheme(theme: Theme | null, tenantId: string | null = null): void {
  const owner = theme === null ? null : tenantId;
  const previousTheme = sessionDefaultRecord?.theme ?? null;
  const previousOwner = sessionDefaultRecord?.tenantId ?? null;
  if (previousTheme === theme && previousOwner === owner) return;
  sessionDefaultRecord = theme === null ? null : { theme, tenantId: owner };
  if (previousTheme === theme) return;
  if (storedPreference() !== null) return;
  document.documentElement.dataset['theme'] = storedTheme();
  notify();
}

/** The acting school's default, or null. */
export function sessionDefaultTheme(): Theme | null {
  return sessionDefaultRecord?.theme ?? null;
}

/**
 * The same default WITH its owner — what a caller needs to decide whether the
 * direction still belongs to the tenant it is acting in.
 */
export function sessionDefault(): Readonly<SessionDefault> | null {
  return sessionDefaultRecord;
}

/** The reader's own choice, or null when they have never made one. */
function storedPreference(): Theme | null {
  try {
    const raw = window.localStorage.getItem(THEME_STORAGE_KEY);
    return isTheme(raw) ? raw : null;
  } catch {
    // Storage can throw (privacy modes); "no preference" is always safe.
    return null;
  }
}

/** The direction to render: the reader's choice, then the school's, then ours. */
export function storedTheme(): Theme {
  return storedPreference() ?? sessionDefaultRecord?.theme ?? DEFAULT_THEME;
}

/** The live theme — what the document is actually rendering right now. */
export function getTheme(): Theme {
  const applied = document.documentElement.dataset['theme'];
  return isTheme(applied) ? applied : DEFAULT_THEME;
}

type Listener = () => void;
const listeners = new Set<Listener>();

function notify(): void {
  for (const l of listeners) l();
}

/** Apply + persist. Safe to call before React mounts. */
export function setTheme(theme: Theme): void {
  document.documentElement.dataset['theme'] = theme;
  try {
    window.localStorage.setItem(THEME_STORAGE_KEY, theme);
  } catch {
    // Non-durable is still functional — the attribute is set for this visit.
  }
  notify();
}

export function toggleTheme(): Theme {
  const next: Theme = getTheme() === 'obsidian' ? 'canvas' : 'obsidian';
  setTheme(next);
  return next;
}

/**
 * Belt-and-braces for the index.html inline script: make the attribute match
 * the stored preference (first paint already used it if the script ran).
 *
 * IT HYDRATES THE SCHOOL'S DEFAULT FIRST, and the order is load-bearing. The
 * pre-paint script reads the brand cache and can land a first-time visitor at
 * a Canvas school in Canvas; if this then applied `storedTheme()` with no
 * session default set, it would immediately overwrite that with Obsidian — a
 * dark flash on every load, caused by the very function that exists to prevent
 * one. Reading the cache for the STORED identity's tenant is the same guess
 * the pre-paint script makes, so the two agree by construction.
 *
 * THE TENANT IT GUESSED IS RECORDED AS THE OWNER, and that is what keeps the
 * guess from being thrown away a frame later: `useBrandSync` clears any
 * direction whose owner is not the tenant it is acting in, so hydrating an
 * ownerless one would hand the hook a mismatch on its very first run and undo
 * this function's whole purpose.
 */
export function initTheme(): void {
  const tenantId = storedTenantId();
  setSessionDefaultTheme(readBrandCache(tenantId)?.defaultTheme ?? null, tenantId);
  document.documentElement.dataset['theme'] = storedTheme();
}

/**
 * Subscribe to theme changes — `useSyncExternalStore`-shaped. Also follows
 * `storage` events so two tabs of the same app stay in agreement.
 */
export function subscribeTheme(listener: Listener): () => void {
  const onStorage = (e: StorageEvent) => {
    if (e.key !== THEME_STORAGE_KEY) return;
    document.documentElement.dataset['theme'] = storedTheme();
    listener();
  };
  listeners.add(listener);
  window.addEventListener('storage', onStorage);
  return () => {
    listeners.delete(listener);
    window.removeEventListener('storage', onStorage);
  };
}
